import type { Content, ContentHref } from '@generalTypes/apiTypes';
import { selectChildren } from '@newStore/documentApi/documentApiSelectors';
import { selectFirstThemeContent } from '@newStore/documentUI/transformContent/krcContentNodeSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import { RequiredType } from '@nodeTypeConfig/configTypes';
import type { ValidationError, ValidationResult } from '../validationTypes';

const emptyArray: Content[] = [];

/**
 * Validates that no two KRC_GOAL_SET_ITEM siblings share the same theme.
 */
export const selectIsGoalSetItemThemeUnique = createTypedSelector(
  (state, href: ContentHref, parentHref: ContentHref | undefined) =>
    parentHref ? selectChildren(state, parentHref) : emptyArray,
  (state, href: ContentHref) => selectFirstThemeContent(state, href),
  (state, href: ContentHref) => href,
  (siblings: Content[], themeContent: Content | null, href: ContentHref): ValidationResult => {
    if (!themeContent || siblings.length <= 1) {
      return true;
    }

    const thisThemeHref = themeContent.$$meta.permalink;

    const hasDuplicate = siblings.some(
      (sibling) => sibling.$$meta.permalink !== href && sibling.themes?.[0] === thisThemeHref,
    );

    if (!hasDuplicate) {
      return true;
    }

    const goalSetName = `${themeContent.identifiers?.[0]} ${themeContent.title}`;

    const error: ValidationError = {
      code: 'krcGoalSetItemDuplicateTheme',
      rule: 'selectIsGoalSetItemThemeUnique',
      message: `Er bestaat al een <strong>doelenset item</strong> voor <strong>${goalSetName}</strong> binnen dit subdomein / deze cluster.`,
      type: RequiredType.ERROR,
    };
    return error;
  },
);
